import { Card, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import { TrashFill, PencilFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import { toast } from "react-toastify";
import AddMediaKitPresentation from "./AddMediaKitPresentation";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";

const MediaKitPresentation = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showAdd, setShowAdd] = useState(false);
  const [toEdit, setToEdit] = useState<any | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const fetchData = () => {
    WebService.getAPI({
      action: `media-kit/get-media-kit?page=${currentPage}&limit=${itemsPerPage}`,
    })
      .then((res: any) => {
        const perm = res?.data?.permissions || {};
        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });

        const formatted = res?.data?.data.map((item: any) => {
          const raw = { ...item }; // 👈 keep raw before overwriting type
          return {
            ...item,
            title: item.title || item.name || "-",
            type: item.type === "FILE" ? "Presentation" : "Logo",
            action: (
              <div>
                {perm.canEdit && (
                  <PencilFill
                    className="me-3 cursor-pointer"
                    title="Edit"
                    onClick={() => {
                      setToEdit(raw); // 👈 pass raw so type="FILE"/"LOGO" is intact
                      setShowAdd(true);
                    }}
                  />
                )}
                {perm.canDelete && (
                  <TrashFill
                    color="#DC143C"
                    className="cursor-pointer"
                    title="Delete"
                    onClick={() => handleDelete(item._id)}
                  />
                )}
              </div>
            ),
          };
        });
        setRows(formatted);
        setTotalCount(res?.data?.pagination?.total);
      })
      .catch(() => {});
  };

  const handleDelete = (id: string) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!itemToDelete) return;
    WebService.deleteAPI({
      action: `media-kit/delete-media-kit/${itemToDelete}`,
    }).then(() => {
      toast.success("Deleted successfully");
      setShowDeleteModal(false);
      setItemToDelete(null);
      fetchData();
    });
  };

  const handleReorder = (newRows: any) => {
    setRows(newRows);
    const reorderedPayload = newRows.map((item: any, index: number) => ({
      _id: item._id,
      order_by: index + 1,
    }));
    WebService.postAPI({
      action: "media-kit/update-order",
      body: reorderedPayload,
    })
      .then(() => {})
      .catch(() => {
        toast.error("Failed to update order");
      });
  };

  return (
    <>
      <AddMediaKitPresentation
        show={showAdd}
        handleClose={() => {
          setShowAdd(false);
          setToEdit(null);
          fetchData();
        }}
        data={toEdit}
        defaultType="FILE"
      />

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={confirmDelete}
        message="Are you sure you want to delete this item?"
      />

      <div className="events-main">
        <Card className="event-card">
          <div className="d-flex justify-content-between">
            <div className="page-title">Media Kit</div>
            {pagePermission.canAdd && (
              <CustomButton
                isLoading={false}
                label="Add"
                style={{ width: 100 }}
                onClick={() => {
                  setToEdit(null);
                  setShowAdd(true);
                }}
              />
            )}
          </div>

          <CustomTableSortable
            columns={[
              { id: "title", label: "Title", minWidth: 170 },
              { id: "type", label: "Type", minWidth: 120 }, // 👈 new column
              { id: "action", label: "Action", minWidth: 100, align: "center" },
            ]}
            rows={rows}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            draggable
            onRowsReorder={handleReorder}
          />
        </Card>
      </div>
    </>
  );
};

export default MediaKitPresentation;
